import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Loader2, ArrowLeft, UserPlus, Send } from "lucide-react";
import { requestRecovery } from "../actions/userActions";
import { FormInput } from "../components/FormInput";
import { Toast } from "./customize/Toast";

const RecoveryScreen = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const [email, setEmail] = useState("");
	const [toast, setToast] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const userLogin = useSelector((state) => state.userLogin);
	const { loading, userInfo } = userLogin;

	const redirect = location.search ? location.search.split("=")[1] : "/";

	useEffect(() => {
		if (userInfo) {
			navigate(redirect);
		}
	}, [navigate, userInfo, redirect]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitted(true);
		try {
			await requestRecovery(email);
			setToast(true);
			setTimeout(() => setToast(false), 6000);
		} catch (error) {
			console.error("Recovery request failed:", error);
		}
	};

	return (
		!userInfo && (
			<>
				<Helmet>
					<meta name="robots" content="noindex,nofollow" />
				</Helmet>

				<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
					<div className="max-w-md mx-auto">
						{/* Header */}
						<div className="text-center mb-8">
							<h2 className="text-3xl font-bold text-gray-900">
								Account Recovery
							</h2>
							<p className="mt-2 text-gray-600">
								Enter your email address and we'll send you instructions to
								reset your password
							</p>
						</div>

						{/* Main Card */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
							<AnimatePresence mode="wait">
								{!submitted ? (
									<motion.form
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -20 }}
										onSubmit={handleSubmit}
										className="space-y-6"
									>
										<FormInput
											label="Email Address"
											type="email"
											icon={Mail}
											required
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											placeholder="Enter your email"
										/>

										<button
											type="submit"
											disabled={loading}
											className="w-full px-4 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
										>
											{loading ? (
												<>
													<Loader2 className="w-5 h-5 animate-spin" />
													Sending...
												</>
											) : (
												<>
													<Send className="w-5 h-5" />
													Send Recovery Email
												</>
											)}
										</button>
									</motion.form>
								) : (
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										className="text-center py-4"
									>
										<div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
											<Mail className="w-8 h-8 text-green-500" />
										</div>
										<h3 className="text-lg font-medium text-gray-900 mb-2">
											Check your email
										</h3>
										<p className="text-gray-600">
											We've sent recovery instructions to
											<br />
											<span className="font-medium">{email}</span>
										</p>
									</motion.div>
								)}
							</AnimatePresence>

							<div className="mt-8 pt-6 border-t border-gray-200">
								<div className="flex flex-col items-center gap-4">
									<Link
										to="/login"
										className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
									>
										<ArrowLeft className="w-4 h-4" />
										Back to Login
									</Link>

									<div className="text-center">
										<p className="text-gray-600">Don't have an account?</p>
										<Link
											to={
												redirect
													? `/register?redirect=${redirect}`
													: "/register"
											}
											className="mt-2 inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
										>
											<UserPlus className="w-4 h-4" />
											Create an account
										</Link>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Toast Notification */}
				<Toast
					show={toast}
					onClose={() => setToast(false)}
					message="Recovery email sent successfully!"
				/>

				{/* Loading Overlay */}
				<AnimatePresence>
					{loading && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
						>
							<div className="flex flex-col items-center gap-4">
								<div className="relative w-32 h-32">
									<img
										src="/images/logoDark.png"
										alt="Logo"
										className="w-full h-full object-contain"
									/>
									<div className="absolute inset-0 border-t-4 border-primary rounded-full animate-spin" />
								</div>
								<p className="text-lg font-medium text-gray-900">
									Sending recovery email...
								</p>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</>
		)
	);
};

export default RecoveryScreen;
